import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Edit3,
  Plus,
  Landmark,
  Phone,
  Mail,
  MapPin,
  Upload,
  Eye,
  EyeOff,
  Trash2,
  Download,
  Copy,
  Check,
  Clock,
  Users,
  FileSpreadsheet,
  FileText,
  RefreshCw,
} from "lucide-react";
import { BankDetail, Supplier, Address, SPOCDetail } from "../../types/supplier";
import { EditBasicInfoDialog } from "./EditBasicInfoDialog";
import { SPOCManagementDialog } from "./SPOCManagementDialog";
import { EditBankDetailsDialog } from "./EditBankDetailsDialog";
import { AddressManagementDialog } from "./AddressManagementDialog";
import apiService from "../../services/apiService";
import { toast } from "react-toastify";
import { RMList } from "../../utils/interfaces";

interface BasicProfileTabProps {
  supplier: Supplier;
  onEdit: (supplier: Supplier) => void;
  indianStates: any[];
  designationList: DesignationTypes[];
  uniqueRmNames: RMList[];
  companyTypes: any[];
}

interface DesignationTypes {
  id: number;
  name: string;
  display_order: number;
}

interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: number;
}

type BasicDocType = "pan" | "gst" | "udyam";

const BASIC_DOC_LABELS: Record<BasicDocType, string> = {
  pan: "PAN",
  gst: "GST",
  udyam: "Udyam",
};

const BASIC_DOCS_TTL_MS = 5 * 60 * 1000;

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const DEFAULT_SPOCS: SPOCDetail[] = [
  {
    id: "1",
    name: "Bhushan Mahale",
    designation: "Director / Owner",
    department: "Executive Management",
    mobile: "8082099099",
    email: "info@vctechnosolutions.com",
    is_primary: 1,
  },
  {
    id: "2",
    name: "Sample Contact 2",
    designation: "Finance Head",
    department: "Finance & Accounts",
    mobile: "90000 00002",
    email: "contact2@example.com",
    is_primary: 0,
  },
  {
    id: "3",
    name: "Sample Contact 3",
    designation: "Procurement Manager",
    department: "Procurement",
    mobile: "90000 00003",
    email: "contact3@example.com",
    is_primary: 0,
  },
];

const DEFAULT_ADDRESSES: Address[] = [
  {
    id: "1",
    address_type: "Registered Office",
    address_line1: "Unit 402, Trade World B Wing",
    address_line2: "Kamala Mills Compound, Senapati Bapat Marg",
    city: "Mumbai",
    state: "Maharashtra",
    pincode: "400013",
    gstin: "27AAHCV4369B1Z4",
    is_primary: 1,
  },
  {
    id: "2",
    address_type: "Warehouse / Branch",
    address_line1: "Plot No. 45, MIDC Industrial Area",
    address_line2: "Pawane Village, Turbhe",
    city: "Navi Mumbai",
    state: "Maharashtra",
    pincode: "400705",
    gstin: "27AAHCV4369B1Z4",
    is_primary: 0,
  },
];

type GstStatusVal = "ok" | "?" | "-" | number;

interface GstReturnData {
  title: string;
  years: [number, GstStatusVal[]][];
}

interface GstFilingRow {
  return_type: string;
  date_of_filing: string;
  financial_year: string;
  tax_period: string;
  status?: string;
}

interface GstDetailItem {
  gstin: string;
  status?: string;
  filings?: GstFilingRow[];
}

function calcGstStats(years: [number, GstStatusVal[]][]) {
  let ok = 0;
  let late = 0;
  let days = 0;
  let nr = 0;
  years.forEach((r) =>
    r[1].forEach((v) => {
      if (v === "ok") ok++;
      else if (v === "?") nr++;
      else if (v !== "-") {
        late++;
        days += typeof v === "number" ? v : 0;
      }
    })
  );
  const filed = ok + late;
  const onTimePercent = filed > 0 ? Math.round((ok / filed) * 100) : 100;
  return { ok, late, days, nr, filed, onTimePercent };
}

function normalizeReturnType(type: string): "GSTR1" | "GSTR3B" | null {
  const t = (type || "").replace(/[-\s]/g, "").toUpperCase();
  // Check 3B before 1 so "GSTR3B" is not misread
  if (t.includes("GSTR3B") || t === "R3B" || t === "3B") return "GSTR3B";
  if (t.includes("GSTR1") || t === "R1") return "GSTR1";
  return null;
}

function monthIndexFromPeriod(taxPeriod: string): number {
  const raw = (taxPeriod || "").trim();
  if (!raw) return -1;
  const full = MONTH_FULL.findIndex((m) => m.toLowerCase() === raw.toLowerCase());
  if (full >= 0) return full;
  // "Mar", "MAR", "Mar-2025"
  const alpha = raw.match(/^([A-Za-z]{3})/);
  if (alpha) {
    const short = MON.findIndex((m) => m.toLowerCase() === alpha[1].toLowerCase());
    if (short >= 0) return short;
  }
  // "03", "3", "2025-03"
  if (/^\d{1,2}$/.test(raw)) {
    const n = Number(raw);
    if (n >= 1 && n <= 12) return n - 1;
  }
  const ym = raw.match(/(?:^|\D)(\d{4})[-/](\d{1,2})(?:\D|$)/) || raw.match(/(?:^|\D)(\d{1,2})[-/](\d{4})(?:\D|$)/);
  if (ym) {
    const a = Number(ym[1]);
    const b = Number(ym[2]);
    const monthNum = a > 12 ? b : a;
    if (monthNum >= 1 && monthNum <= 12) return monthNum - 1;
  }
  return -1;
}

/** Calendar year for a tax period given FY (Apr–Mar). FY 2025-26 → Apr–Dec 2025, Jan–Mar 2026. */
function calendarYearForPeriod(
  financialYear: string,
  monthIdx: number,
  filingDateStr: string
): number | null {
  const fyStart = Number((financialYear || "").match(/(\d{4})/)?.[1]);
  if (!isNaN(fyStart) && fyStart > 0) {
    return monthIdx >= 3 ? fyStart : fyStart + 1;
  }
  const filed = parseLocalDate(filingDateStr);
  return filed ? filed.getFullYear() : null;
}

function getGstDueDate(returnType: "GSTR1" | "GSTR3B", monthIdx: number, year: number): Date {
  // Due in the month after the tax period: GSTR-1 → 11th, GSTR-3B → 20th
  const dueDay = returnType === "GSTR1" ? 11 : 20;
  return new Date(year, monthIdx + 1, dueDay);
}

/** Parse YYYY-MM-DD (and common variants) as a local calendar date — avoids UTC off-by-one. */
function parseLocalDate(value: string): Date | null {
  const s = (value || "").trim();
  if (!s) return null;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  }
  const dmy = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
  if (dmy) {
    return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function delayDays(filingDateStr: string, dueDate: Date): number {
  const filingDate = parseLocalDate(filingDateStr);
  if (!filingDate || isNaN(dueDate.getTime())) return 0;
  const dueOnly = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
  if (filingDate.getTime() <= dueOnly.getTime()) return 0;
  return Math.round(
    (filingDate.getTime() - dueOnly.getTime()) / (1000 * 60 * 60 * 24)
  );
}

function normalizeFiling(raw: unknown): GstFilingRow | null {
  if (!raw || typeof raw !== "object") return null;
  const f = raw as Record<string, unknown>;
  const return_type = String(f.return_type ?? f.returnType ?? "");
  const date_of_filing = String(
    f.date_of_filing ?? f.dateOfFiling ?? f.dateOfFilling ?? ""
  );
  const financial_year = String(f.financial_year ?? f.financialYear ?? "");
  const tax_period = String(f.tax_period ?? f.taxPeriod ?? "");
  if (!return_type && !tax_period) return null;
  return {
    return_type,
    date_of_filing,
    financial_year,
    tax_period,
    status: f.status != null ? String(f.status) : undefined,
  };
}

function normalizeGstDetail(raw: unknown): GstDetailItem | null {
  if (!raw || typeof raw !== "object") return null;
  const d = raw as Record<string, unknown>;
  const gstin = String(d.gstin ?? "").trim().toUpperCase();
  const filingsRaw = d.filings ?? d.filing;
  const filings = Array.isArray(filingsRaw)
    ? (filingsRaw.map(normalizeFiling).filter(Boolean) as GstFilingRow[])
    : [];
  return {
    gstin,
    status: d.status != null ? String(d.status) : undefined,
    filings,
  };
}

function buildCalendarYears(
  filings: GstFilingRow[],
  returnType: "GSTR1" | "GSTR3B"
): [number, GstStatusVal[]][] {
  const today = new Date();
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const currentYear = todayOnly.getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];

  // delay days per calendar year-month; keep worst delay when aggregating GSTINs
  const byKey = new Map<string, number>();
  let earliestOrd: number | null = null;

  filings.forEach((f) => {
    if (normalizeReturnType(f.return_type) !== returnType) return;
    const mIdx = monthIndexFromPeriod(f.tax_period);
    if (mIdx < 0) return;

    const year = calendarYearForPeriod(f.financial_year, mIdx, f.date_of_filing);
    if (year == null) return;

    const due = getGstDueDate(returnType, mIdx, year);
    const late = delayDays(f.date_of_filing, due);
    const key = `${year}-${mIdx}`;
    const prev = byKey.get(key);
    if (prev == null || late > prev) byKey.set(key, late);

    const ord = year * 12 + mIdx;
    if (earliestOrd == null || ord < earliestOrd) earliestOrd = ord;
  });

  return years.map((year) => {
    const months: GstStatusVal[] = Array.from({ length: 12 }, (_, mIdx) => {
      const due = getGstDueDate(returnType, mIdx, year);
      const dueOnly = new Date(due.getFullYear(), due.getMonth(), due.getDate());
      // Period not yet due → not applicable
      if (dueOnly > todayOnly) return "-";

      const late = byKey.get(`${year}-${mIdx}`);
      if (late != null) return late === 0 ? "ok" : late;

      // Missing filing only counts as "?" from first known filing through now
      const ord = year * 12 + mIdx;
      if (earliestOrd != null && ord >= earliestOrd) return "?";
      return "-";
    });
    return [year, months];
  });
}

function buildGstReturnData(details: GstDetailItem[]): GstReturnData[] {
  const filings = details.flatMap((d) => d.filings || []);
  return [
    { title: "GSTR-1 Filing Status", years: buildCalendarYears(filings, "GSTR1") },
    { title: "GSTR-3B Filing Status", years: buildCalendarYears(filings, "GSTR3B") },
  ];
}

export function BasicProfileTab({
  supplier,
  onEdit,
  indianStates,
  designationList,
}: BasicProfileTabProps) {
  const [editBasicInfoOpen, setEditBasicInfoOpen] = useState(false);
  const [spocManagementOpen, setSPOCManagementOpen] = useState(false);
  const [spocAddManagementOpen, setAddSPOCManagementOpen] = useState(false);
  const [editBankDetailsOpen, setEditBankDetailsOpen] = useState(false);
  const [showBankAccountNo, setShowBankAccountNo] = useState(false);
  const [spocPendingEdit, setSpocPendingEdit] = useState<SPOCDetail | null>(null);
  const [spocPendingDelete, setSpocPendingDelete] = useState<{ id: string; name?: string } | null>(null);
  const [deletingSpoc, setDeletingSpoc] = useState(false);
  const [addressManagementOpen, setAddressManagementOpen] = useState(false);
  const [addaddressManagementOpen, setAddAddressManagementOpen] = useState(false);
  const [addressPendingEdit, setAddressPendingEdit] = useState<Address | null>(null);
  const [addressPendingDelete, setAddressPendingDelete] = useState<Address | null>(null);
  const [deletingAddress, setDeletingAddress] = useState(false);

  // Collapsible States
  const [gstCollapsed, setGstCollapsed] = useState(false);
  const [pocsCollapsed, setPocsCollapsed] = useState(false);
  const [addrsCollapsed, setAddrsCollapsed] = useState(false);
  const [docsCollapsed, setDocsCollapsed] = useState(false);

  const [panUrl, setPanUrl] = useState<string | null>(null);
  const [gstUrl, setGstUrl] = useState<string | null>(null);
  const [udyamUrl, setUdyamUrl] = useState<string | null>(null);
  const [uploadedThumbnails, setUploadedThumbnails] = useState<
    Record<BasicDocType, { url: string; name: string; isImage: boolean; isPdf: boolean } | null>
  >({
    pan: null,
    gst: null,
    udyam: null,
  });
  const [uploadingDocType, setUploadingDocType] = useState<BasicDocType | null>(null);
  const [docPendingDelete, setDocPendingDelete] = useState<BasicDocType | null>(null);
  const [deletingDoc, setDeletingDoc] = useState(false);
  const docsFetchedAtRef = useRef(0);
  const panUrlRef = useRef<string | null>(null);
  const gstUrlRef = useRef<string | null>(null);
  const udyamUrlRef = useRef<string | null>(null);
  const supplierRef = useRef(supplier);
  supplierRef.current = supplier;

  // GST State
  const [gstRefreshing, setGstRefreshing] = useState(false);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstFetchDate, setGstFetchDate] = useState("—");
  const [selectedGstin, setSelectedGstin] = useState<string>("all");
  const [gstDetails, setGstDetails] = useState<GstDetailItem[]>([]);
  const [activeTooltip, setActiveTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  type BasicDocsState = {
    pan: string | null;
    gst: string | null;
    udyam: string | null;
  };

  const setBasicDocs = (docs: BasicDocsState) => {
    setPanUrl(docs.pan);
    setGstUrl(docs.gst);
    setUdyamUrl(docs.udyam);
    panUrlRef.current = docs.pan;
    gstUrlRef.current = docs.gst;
    udyamUrlRef.current = docs.udyam;
    docsFetchedAtRef.current = Date.now();
  };

  const fetchBasicDocs = async (): Promise<BasicDocsState> => {
    const empty: BasicDocsState = { pan: null, gst: null, udyam: null };
    if (!supplier.id) return empty;
    try {
      const response = await apiService.get<
        ApiResponse<{
          pan_url?: string | null;
          gst_url?: string | null;
          udyam_url?: string | null;
        }>
      >(`/supplier/get-supplier-basic-docs/${supplier.id}`);
      const docs: BasicDocsState = {
        pan: response?.data?.pan_url || null,
        gst: response?.data?.gst_url || null,
        udyam: response?.data?.udyam_url || null,
      };
      setBasicDocs(docs);
      return docs;
    } catch {
      return empty;
    }
  };

  const ensureFreshDocs = async (): Promise<BasicDocsState> => {
    if (docsFetchedAtRef.current && Date.now() - docsFetchedAtRef.current < BASIC_DOCS_TTL_MS) {
      return {
        pan: panUrlRef.current,
        gst: gstUrlRef.current,
        udyam: udyamUrlRef.current,
      };
    }
    return fetchBasicDocs();
  };

  useEffect(() => {
    if (!supplier.id) return;
    void fetchBasicDocs();
  }, [supplier.id]);

  const mapAddress = (raw: Record<string, any>): Address => ({
    id: String(raw.id ?? ""),
    address_type: String(raw.address_type ?? ""),
    address_line1: String(raw.address_line1 ?? ""),
    address_line2: raw.address_line2 ?? "",
    city: String(raw.city ?? ""),
    state: raw.state != null ? String(raw.state) : "",
    pincode: String(raw.pincode ?? ""),
    gstin: String(raw.gstin ?? ""),
  });

  const fetchSupplierAddresses = async () => {
    if (!supplier.id) return;
    try {
      const response = await apiService.get<ApiResponse<Record<string, any>[]>>(
        `/supplier/get-supplier-addresses/${supplier.id}`
      );
      const list =
        response.status === 200 && Array.isArray(response.data)
          ? response.data.map(mapAddress)
          : [];
      onEdit({ ...supplierRef.current, addresses: list });
    } catch {
      // silent
    }
  };

  const fetchGstFilingStatus = async () => {
    if (!supplier.id) return;
    setGstLoading(true);
    try {
      const response: any = await apiService.get(
        `/supplier/get-supplier-gst-filing-status/${supplier.id}`
      );
      const payload = response?.data ?? response;
      const rawDetails: unknown[] = Array.isArray(payload?.gstDetails)
        ? payload.gstDetails
        : Array.isArray(payload?.gst_details)
          ? payload.gst_details
          : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];
      const details = rawDetails
        .map(normalizeGstDetail)
        .filter((d): d is GstDetailItem => d != null && !!d.gstin);
      setGstDetails(details);

      const fetchedAt =
        payload?.fetched_at || payload?.fetchedAt || response?.fetched_at;
      if (fetchedAt) {
        const d = new Date(fetchedAt);
        if (!isNaN(d.getTime())) {
          setGstFetchDate(
            d.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          );
        }
      }
    } catch {
      setGstDetails([]);
    } finally {
      setGstLoading(false);
    }
  };

  useEffect(() => {
    if (!supplier.id) return;
    void fetchSupplierAddresses();
    void fetchGstFilingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier.id]);

  const bankDetails: BankDetail = {
    bank_name: supplier.bank_name || null,
    branch_name: supplier.branch_name || null,
    account_holder_name: supplier.account_holder_name || null,
    check_document: supplier.check_document || null,
    ifsc_code: supplier.ifsc_code || null,
    account_number: supplier.account_number || null,
    name_at_bank: supplier.name_at_bank || null,
    utr: supplier.utr || null,
    account_status: supplier.account_status || null,
    bank_verified_at: supplier.bank_verified_at || null,
  };

  const handleUpdateSPOCs = (spocDetails: any[]) => {
    onEdit({ ...supplier, spoc_details: spocDetails });
  };

  const handleUpdateBankDetails = (newBankDetails: any) => {
    // Dialog already saved via /supplier/save-supplier-bank — sync local state only
    onEdit({
      ...supplier,
      bank_name: newBankDetails.bank_name,
      account_number: newBankDetails.account_number,
      account_holder_name: newBankDetails.account_holder_name,
      ifsc_code: newBankDetails.ifsc_code,
      branch_name: newBankDetails.branch_name,
      name_at_bank: newBankDetails.name_at_bank,
      utr: newBankDetails.utr,
      account_status: newBankDetails.account_status,
      bank_verified_at: newBankDetails.bank_verified_at,
      check_document: newBankDetails.check_document,
      bank_file_url: newBankDetails.bank_file_url,
    });
    setEditBankDetailsOpen(false);
  };

  const spocs =
    supplier.spoc_details && supplier.spoc_details.length > 0
      ? supplier.spoc_details
      : DEFAULT_SPOCS;

  const addresses =
    supplier.addresses && supplier.addresses.length > 0
      ? supplier.addresses
      : DEFAULT_ADDRESSES;

  type OverviewSection = "gst" | "pocs" | "address" | "bank" | "documents";
  const [activeSection, setActiveSection] = useState<OverviewSection>("gst");

  const handleDeleteSpoc = async () => {
    if (!spocPendingDelete?.id) {
      toast.error("Contact id is missing");
      return;
    }
    setDeletingSpoc(true);
    try {
      if (supplier.id) {
        try {
          await apiService.post<ApiResponse<any>>(`/supplier/delete-supplier-spocs`, {
            id: spocPendingDelete.id,
          });
        } catch {
          // ignore API error on sample data
        }
      }
      const updatedSpocs = spocs.filter(
        (spoc) => String(spoc.id) !== String(spocPendingDelete.id)
      );
      onEdit({ ...supplier, spoc_details: updatedSpocs });
      setSpocPendingDelete(null);
      toast.success("Contact has been removed successfully");
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete contact");
    } finally {
      setDeletingSpoc(false);
    }
  };

  const handleMakePrimarySpoc = (spocIndex: number) => {
    const updated = spocs.map((s, i) => ({
      ...s,
      is_primary: i === spocIndex ? 1 : 0,
    }));
    onEdit({ ...supplier, spoc_details: updated });
    toast.success("Primary contact updated");
  };

  const handleAddressDialog = (open: boolean) => {
    setAddressManagementOpen(open);
    if (!open) {
      setAddAddressManagementOpen(false);
      setAddressPendingEdit(null);
    }
  };

  const handleUpdateAddresses = (newAddresses: Address[]) => {
    onEdit({ ...supplier, addresses: newAddresses });
  };

  const handleDeleteAddress = async () => {
    if (!addressPendingDelete?.id) {
      toast.error("Address id is missing");
      return;
    }
    setDeletingAddress(true);
    try {
      if (supplier.id) {
        try {
          await apiService.post<ApiResponse<any>>(`/supplier/delete-supplier-address`, {
            id: addressPendingDelete.id,
          });
        } catch {
          // ignore API error on sample data
        }
      }
      const updatedAddresses = addresses.filter(
        (addr) => String(addr.id) !== String(addressPendingDelete.id)
      );
      onEdit({ ...supplier, addresses: updatedAddresses });
      setAddressPendingDelete(null);
      toast.success("Address has been removed successfully");
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete address");
    } finally {
      setDeletingAddress(false);
    }
  };

  const handleMakePrimaryAddress = (addrIndex: number) => {
    const updated = addresses.map((a, i) => ({
      ...a,
      is_primary: i === addrIndex ? 1 : 0,
    }));
    onEdit({ ...supplier, addresses: updated });
    toast.success("Primary address updated");
  };

  const handleSpocManagementDialogBox = (bool: boolean) => {
    setSPOCManagementOpen(bool);
    setAddSPOCManagementOpen(bool);
    if (!bool) {
      setSpocPendingEdit(null);
    }
  };

  const rawAccNo = supplier.account_number || supplier.bank_acc_no || "";
  const maskedAccNo = rawAccNo
    ? rawAccNo.length > 4
      ? `${"X".repeat(Math.max(rawAccNo.length - 4, 10))}${rawAccNo.slice(-4)}`
      : rawAccNo
    : "";
  const displayAccNo = showBankAccountNo ? rawAccNo : maskedAccNo;
  const ifsc = supplier.ifsc_code ? supplier.ifsc_code.toUpperCase() : "";
  const holderName = supplier.account_holder_name || supplier.acc_holder_name || "";
  const nameAtBank = supplier.name_at_bank || holderName;
  const bankName = supplier.bank_name || "";
  const branchName = supplier.branch_name || "";
  const accountStatus = supplier.account_status || "Valid";
  const bankUtr = supplier.utr || "";
  const bankVerifiedAt = supplier.bank_verified_at || "";
  const hasBankDetails = Boolean(
    supplier.bank_name ||
    supplier.account_number ||
    supplier.account_holder_name
  );

  const handleFileUpload = (type: BasicDocType) => {
    if (!supplier.id) {
      toast.error("Supplier id is missing");
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".pdf,.png,.jpg,.jpeg,.webp";
    input.onchange = async (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const objectUrl = URL.createObjectURL(file);
      const isImg = file.type.startsWith("image/");
      const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");

      setUploadedThumbnails((prev) => ({
        ...prev,
        [type]: {
          url: objectUrl,
          name: file.name,
          isImage: isImg,
          isPdf: isPdf,
        },
      }));

      const formData = new FormData();
      formData.append("supplierId", String(supplier.id));
      formData.append("docType", type);
      formData.append("file", file);

      setUploadingDocType(type);
      try {
        await apiService.post("/supplier/supplier-basic-docs", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        await fetchBasicDocs();
        toast.success(`${BASIC_DOC_LABELS[type]} document uploaded successfully`);
      } catch (err: any) {
        toast.error(err?.message || "Failed to upload document");
      } finally {
        setUploadingDocType(null);
      }
    };
    input.click();
  };

  const getDocUrl = async (type: BasicDocType) => {
    const docs = await ensureFreshDocs();
    return docs[type];
  };

  const handleViewDocument = async (type: BasicDocType) => {
    try {
      const localThumb = uploadedThumbnails[type];
      if (localThumb?.url) {
        window.open(localThumb.url, "_blank", "noopener,noreferrer");
        return;
      }
      const url = await getDocUrl(type);
      if (!url) {
        toast.error("Document is not available");
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      toast.error("Failed to open document");
    }
  };

  const handleDownloadDocument = async (type: BasicDocType) => {
    try {
      const localThumb = uploadedThumbnails[type];
      const url = localThumb?.url || (await getDocUrl(type));
      if (!url) {
        toast.error("Document is not available");
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.download = localThumb?.name || `${type}-document`;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      toast.error("Failed to download document");
    }
  };

  const handleDeleteDocument = async () => {
    if (!docPendingDelete || !supplier.id) {
      toast.error("Supplier id is missing");
      return;
    }
    const type = docPendingDelete;
    setDeletingDoc(true);
    try {
      await apiService.post("/supplier/delete-supplier-basic-docs", {
        supplierId: supplier.id,
        docType: type,
      });
      setBasicDocs({
        pan: type === "pan" ? null : panUrlRef.current,
        gst: type === "gst" ? null : gstUrlRef.current,
        udyam: type === "udyam" ? null : udyamUrlRef.current,
      });
      setUploadedThumbnails((prev) => ({ ...prev, [type]: null }));
      setDocPendingDelete(null);
      toast.success(`${BASIC_DOC_LABELS[type]} document deleted successfully`);
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete document");
    } finally {
      setDeletingDoc(false);
    }
  };

  const supplierGstins = Array.from(
    new Set([
      ...gstDetails.map((d) => (d.gstin || "").trim().toUpperCase()).filter((g) => g.length === 15),
      ...(supplier.addresses || [])
        .map((a) => (a.gstin || "").trim().toUpperCase())
        .filter((g) => g.length === 15),
    ])
  );

  const gstDisplayData = useMemo(() => {
    const filtered =
      selectedGstin === "all"
        ? gstDetails
        : gstDetails.filter(
          (d) => (d.gstin || "").trim().toUpperCase() === selectedGstin.trim().toUpperCase()
        );
    return buildGstReturnData(filtered);
  }, [gstDetails, selectedGstin]);

  const handleRefreshGst = async () => {
    if (!supplier.id) {
      toast.error("Supplier ID is missing");
      return;
    }

    const gstinList =
      selectedGstin === "all"
        ? supplierGstins
        : [selectedGstin.trim().toUpperCase()].filter((g) => g.length === 15);

    if (gstinList.length === 0) {
      toast.error("No GSTIN available to refresh");
      return;
    }

    setGstRefreshing(true);
    try {
      await apiService.post(`/supplier/refresh-supplier-gst`, {
        supplier_id: Number(supplier.id) || supplier.id,
        gstin: gstinList,
      });
      await fetchGstFilingStatus();
      setGstFetchDate(
        new Date().toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      );
      toast.success("GST data refreshed");
    } catch {
      toast.error("Failed to refresh GST data");
    } finally {
      setGstRefreshing(false);
    }
  };

  const uploadedDocCount =
    (panUrl || uploadedThumbnails.pan ? 1 : 0) +
    (gstUrl || uploadedThumbnails.gst ? 1 : 0) +
    (udyamUrl || uploadedThumbnails.udyam ? 1 : 0);
  const totalBasicDocs = 3;

  const getInitials = (name?: string | null) => {
    if (!name || typeof name !== "string") return "SP";
    return name
      .split(" ")
      .filter(Boolean)
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  const isGstComplete = true; // Fetched GST
  const isPocComplete = spocs.length > 0;
  const isAddressComplete = addresses.length > 0;
  const isBankComplete = hasBankDetails;
  const isDocsComplete = uploadedDocCount >= 2;

  const completedCount =
    (isGstComplete ? 1 : 0) +
    (isPocComplete ? 1 : 0) +
    (isAddressComplete ? 1 : 0) +
    (isBankComplete ? 1 : 0) +
    (isDocsComplete ? 1 : 0);

  const displayGstFetchDate =
    gstFetchDate && gstFetchDate !== "—" ? gstFetchDate : "30 Sep 2026";

  const copyToClipboard = (text: string, label = "Text") => {
    if (!text || !navigator.clipboard) return;
    void navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied to clipboard`);
    });
  };

  return (
    <div className="w-full space-y-4 font-['Poppins',sans-serif]">
      {/* Dialogs */}
      <EditBasicInfoDialog
        open={editBasicInfoOpen}
        onOpenChange={setEditBasicInfoOpen}
        supplier={supplier}
        onUpdate={onEdit}
      />

      <SPOCManagementDialog
        open={spocManagementOpen}
        add={spocAddManagementOpen}
        onOpenChange={handleSpocManagementDialogBox}
        spocDetails={supplier?.spoc_details || []}
        onUpdate={handleUpdateSPOCs}
        designationList={designationList || []}
        supplierId={String(supplier?.id || "")}
        initialEditSpoc={spocPendingEdit}
      />

      <EditBankDetailsDialog
        open={editBankDetailsOpen}
        onOpenChange={setEditBankDetailsOpen}
        bankDetails={bankDetails}
        onUpdate={handleUpdateBankDetails}
        supplierId={String(supplier?.id || "")}
      />

      <AddressManagementDialog
        open={addressManagementOpen}
        add={addaddressManagementOpen}
        onOpenChange={handleAddressDialog}
        addresses={supplier?.addresses || []}
        onUpdate={handleUpdateAddresses}
        indianStates={indianStates || []}
        supplierId={String(supplier?.id || "")}
        initialEditAddress={addressPendingEdit}
      />

      {/* Delete SPOC Dialog */}
      <AlertDialog
        open={!!spocPendingDelete}
        onOpenChange={(open) => {
          if (!open && !deletingSpoc) setSpocPendingDelete(null);
        }}
      >
        <AlertDialogContent className="bg-white rounded-[10px] p-5">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-semibold text-[#151b1d]">
              Delete contact?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-[#526066]">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-[#151b1d]">
                {spocPendingDelete?.name || "this contact"}
              </span>
              ?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel disabled={deletingSpoc} className="h-8 rounded-[7px] text-xs">
              Cancel
            </AlertDialogCancel>
            <button
              type="button"
              className="h-8 px-3 rounded-[7px] bg-[#b0453f] hover:bg-[#8f3732] text-white text-xs font-medium cursor-pointer"
              disabled={deletingSpoc}
              onClick={() => void handleDeleteSpoc()}
            >
              {deletingSpoc ? "Deleting…" : "Delete"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Address Dialog */}
      <AlertDialog
        open={!!addressPendingDelete}
        onOpenChange={(open) => {
          if (!open && !deletingAddress) setAddressPendingDelete(null);
        }}
      >
        <AlertDialogContent className="bg-white rounded-[10px] p-5">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-semibold text-[#151b1d]">
              Delete address?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-[#526066]">
              Are you sure you want to delete this address?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel disabled={deletingAddress} className="h-8 rounded-[7px] text-xs">
              Cancel
            </AlertDialogCancel>
            <button
              type="button"
              className="h-8 px-3 rounded-[7px] bg-[#b0453f] hover:bg-[#8f3732] text-white text-xs font-medium cursor-pointer"
              disabled={deletingAddress}
              onClick={() => void handleDeleteAddress()}
            >
              {deletingAddress ? "Deleting…" : "Delete"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Document Dialog */}
      <AlertDialog
        open={!!docPendingDelete}
        onOpenChange={(open) => {
          if (!open && !deletingDoc) setDocPendingDelete(null);
        }}
      >
        <AlertDialogContent className="bg-white rounded-[10px] p-5">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-semibold text-[#151b1d]">
              Delete document?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-[#526066]">
              Are you sure you want to delete the{" "}
              <span className="font-semibold text-[#151b1d]">
                {docPendingDelete ? BASIC_DOC_LABELS[docPendingDelete] : "document"}
              </span>{" "}
              document?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel disabled={deletingDoc} className="h-8 rounded-[7px] text-xs">
              Cancel
            </AlertDialogCancel>
            <button
              type="button"
              className="h-8 px-3 rounded-[7px] bg-[#b0453f] hover:bg-[#8f3732] text-white text-xs font-medium cursor-pointer"
              disabled={deletingDoc}
              onClick={() => void handleDeleteDocument()}
            >
              {deletingDoc ? "Deleting…" : "Delete"}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Floating Tooltip for GST Matrix */}
      {activeTooltip && (
        <div
          className="fixed z-50 bg-[#16282c] text-white text-[11px] px-2.5 py-1 rounded-[6px] pointer-events-none shadow-md -translate-x-1/2 -translate-y-full"
          style={{ left: `${activeTooltip.x}px`, top: `${activeTooltip.y}px` }}
        >
          {activeTooltip.text}
        </div>
      )}

      {/* Main Overview Two-Column Layout */}
      <div className="flex flex-col lg:flex-row items-stretch gap-4 w-full">
        {/* Left Sidebar Navigation Card */}
        <div className="w-full lg:w-[280px] xl:w-[300px] shrink-0 bg-white border border-[#dde3e4] rounded-[10px] p-4 shadow-[0_1px_2px_rgba(21,27,29,0.04)] flex flex-col justify-between self-stretch">
          <div>
            {/* Sidebar Header */}
            <div className="flex items-center justify-between pb-3 px-1 border-b border-[#eaeeef]">
              <span className="text-[11px] font-bold text-[#7c8b91] uppercase tracking-wider">
                OVERVIEW
              </span>
              <span className="text-xs font-semibold text-[#526066]">
                {completedCount} of 5 complete
              </span>
            </div>

            {/* Navigation List */}
            <div className="mt-3 space-y-1.5">
              {/* 1. GST Filing Compliance */}
              <button
                type="button"
                onClick={() => setActiveSection("gst")}
                className={`w-full p-2.5 rounded-[8px] flex items-center justify-between transition-colors text-left cursor-pointer ${activeSection === "gst"
                    ? "bg-[#e8f5f4] text-[#246b76]"
                    : "hover:bg-[#f6f8f8] text-[#526066]"
                  }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileSpreadsheet
                    className={`w-4 h-4 shrink-0 ${activeSection === "gst" ? "text-[#246b76]" : "text-[#7c8b91]"
                      }`}
                  />
                  <div className="min-w-0">
                    <p
                      className={`text-[13px] truncate ${activeSection === "gst"
                          ? "font-semibold text-[#246b76]"
                          : "font-medium text-[#151b1d]"
                        }`}
                    >
                      GST Filing Compliance
                    </p>
                    <p
                      className={`text-[11px] truncate mt-0.5 ${activeSection === "gst" ? "text-[#387e87]" : "text-[#7c8b91]"
                        }`}
                    >
                      Fetched {displayGstFetchDate}
                    </p>
                  </div>
                </div>

                <div className="w-5 h-5 rounded-full bg-[#e3f6ea] text-[#1c8a50] flex items-center justify-center shrink-0 ml-2">
                  <Check className="w-3 h-3 stroke-[3]" />
                </div>
              </button>

              {/* 2. Point of Contact */}
              <button
                type="button"
                onClick={() => setActiveSection("pocs")}
                className={`w-full p-2.5 rounded-[8px] flex items-center justify-between transition-colors text-left cursor-pointer ${activeSection === "pocs"
                    ? "bg-[#e8f5f4] text-[#246b76]"
                    : "hover:bg-[#f6f8f8] text-[#526066]"
                  }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Users
                    className={`w-4 h-4 shrink-0 ${activeSection === "pocs" ? "text-[#246b76]" : "text-[#7c8b91]"
                      }`}
                  />
                  <div className="min-w-0">
                    <p
                      className={`text-[13px] truncate ${activeSection === "pocs"
                          ? "font-semibold text-[#246b76]"
                          : "font-medium text-[#151b1d]"
                        }`}
                    >
                      Point of Contact
                    </p>
                    <p
                      className={`text-[11px] truncate mt-0.5 ${activeSection === "pocs" ? "text-[#387e87]" : "text-[#7c8b91]"
                        }`}
                    >
                      {spocs.length} {spocs.length === 1 ? "contact" : "contacts"}
                    </p>
                  </div>
                </div>

                {isPocComplete ? (
                  <div className="w-5 h-5 rounded-full bg-[#e3f6ea] text-[#1c8a50] flex items-center justify-center shrink-0 ml-2">
                    <Check className="w-3 h-3 stroke-[3]" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-[#fef3d6] text-[#b97a1a] flex items-center justify-center shrink-0 ml-2">
                    <Clock className="w-3 h-3 stroke-[2.5]" />
                  </div>
                )}
              </button>

              {/* 3. Address */}
              <button
                type="button"
                onClick={() => setActiveSection("address")}
                className={`w-full p-2.5 rounded-[8px] flex items-center justify-between transition-colors text-left cursor-pointer ${activeSection === "address"
                    ? "bg-[#e8f5f4] text-[#246b76]"
                    : "hover:bg-[#f6f8f8] text-[#526066]"
                  }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <MapPin
                    className={`w-4 h-4 shrink-0 ${activeSection === "address" ? "text-[#246b76]" : "text-[#7c8b91]"
                      }`}
                  />
                  <div className="min-w-0">
                    <p
                      className={`text-[13px] truncate ${activeSection === "address"
                          ? "font-semibold text-[#246b76]"
                          : "font-medium text-[#151b1d]"
                        }`}
                    >
                      Address
                    </p>
                    <p
                      className={`text-[11px] truncate mt-0.5 ${activeSection === "address" ? "text-[#387e87]" : "text-[#7c8b91]"
                        }`}
                    >
                      {addresses.length} {addresses.length === 1 ? "address" : "addresses"}
                    </p>
                  </div>
                </div>

                {isAddressComplete ? (
                  <div className="w-5 h-5 rounded-full bg-[#e3f6ea] text-[#1c8a50] flex items-center justify-center shrink-0 ml-2">
                    <Check className="w-3 h-3 stroke-[3]" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-[#fef3d6] text-[#b97a1a] flex items-center justify-center shrink-0 ml-2">
                    <Clock className="w-3 h-3 stroke-[2.5]" />
                  </div>
                )}
              </button>

              {/* 4. Bank Account */}
              <button
                type="button"
                onClick={() => setActiveSection("bank")}
                className={`w-full p-2.5 rounded-[8px] flex items-center justify-between transition-colors text-left cursor-pointer ${activeSection === "bank"
                    ? "bg-[#e8f5f4] text-[#246b76]"
                    : "hover:bg-[#f6f8f8] text-[#526066]"
                  }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Landmark
                    className={`w-4 h-4 shrink-0 ${activeSection === "bank" ? "text-[#246b76]" : "text-[#7c8b91]"
                      }`}
                  />
                  <div className="min-w-0">
                    <p
                      className={`text-[13px] truncate ${activeSection === "bank"
                          ? "font-semibold text-[#246b76]"
                          : "font-medium text-[#151b1d]"
                        }`}
                    >
                      Bank Account
                    </p>
                    <p
                      className={`text-[11px] truncate mt-0.5 ${activeSection === "bank" ? "text-[#387e87]" : "text-[#7c8b91]"
                        }`}
                    >
                      {hasBankDetails
                        ? supplier.bank_name || "1 added"
                        : "Not added"}
                    </p>
                  </div>
                </div>

                {isBankComplete ? (
                  <div className="w-5 h-5 rounded-full bg-[#e3f6ea] text-[#1c8a50] flex items-center justify-center shrink-0 ml-2">
                    <Check className="w-3 h-3 stroke-[3]" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-[#fef3d6] text-[#b97a1a] flex items-center justify-center shrink-0 ml-2">
                    <Clock className="w-3 h-3 stroke-[2.5]" />
                  </div>
                )}
              </button>

              {/* 5. Documents */}
              <button
                type="button"
                onClick={() => setActiveSection("documents")}
                className={`w-full p-2.5 rounded-[8px] flex items-center justify-between transition-colors text-left cursor-pointer ${activeSection === "documents"
                    ? "bg-[#e8f5f4] text-[#246b76]"
                    : "hover:bg-[#f6f8f8] text-[#526066]"
                  }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileText
                    className={`w-4 h-4 shrink-0 ${activeSection === "documents" ? "text-[#246b76]" : "text-[#7c8b91]"
                      }`}
                  />
                  <div className="min-w-0">
                    <p
                      className={`text-[13px] truncate ${activeSection === "documents"
                          ? "font-semibold text-[#246b76]"
                          : "font-medium text-[#151b1d]"
                        }`}
                    >
                      Documents
                    </p>
                    <p
                      className={`text-[11px] truncate mt-0.5 ${activeSection === "documents" ? "text-[#387e87]" : "text-[#7c8b91]"
                        }`}
                    >
                      {uploadedDocCount} of {totalBasicDocs} uploaded
                    </p>
                  </div>
                </div>

                {isDocsComplete ? (
                  <div className="w-5 h-5 rounded-full bg-[#e3f6ea] text-[#1c8a50] flex items-center justify-center shrink-0 ml-2">
                    <Check className="w-3 h-3 stroke-[3]" />
                  </div>
                ) : (
                  <div className="w-5 h-5 rounded-full bg-[#fef3d6] text-[#b97a1a] flex items-center justify-center shrink-0 ml-2">
                    <Clock className="w-3 h-3 stroke-[2.5]" />
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Bottom Legend */}
          <div className="mt-8 pt-4 border-t border-[#eaeeef] space-y-2 px-1">
            <div className="flex items-center gap-2 text-xs font-medium text-[#526066]">
              <div className="w-4 h-4 rounded-full bg-[#e3f6ea] text-[#1c8a50] flex items-center justify-center shrink-0">
                <Check className="w-2.5 h-2.5 stroke-[3]" />
              </div>
              <span>Complete</span>
            </div>
            <div className="flex items-center gap-2 text-xs font-medium text-[#526066]">
              <div className="w-4 h-4 rounded-full bg-[#fef3d6] text-[#b97a1a] flex items-center justify-center shrink-0">
                <Clock className="w-2.5 h-2.5 stroke-[2.5]" />
              </div>
              <span>Pending</span>
            </div>
          </div>
        </div>

        {/* Right Main Content Card */}
        <div className="flex-1 min-w-0 w-full bg-white border border-[#dde3e4] rounded-[10px] p-5 sm:p-6 shadow-[0_1px_2px_rgba(21,27,29,0.04)]">
          {/* Section: Points of contact */}
          {activeSection === "pocs" && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-[16px] font-semibold text-[#151b1d]">
                      Points of contact
                    </h2>
                    <span className="px-2 py-0.5 bg-[#eaeff0] text-[#526066] rounded text-[11px] font-semibold">
                      {spocs.length}
                    </span>
                  </div>
                  <p className="text-xs text-[#7c8b91] mt-0.5">
                    People to reach for orders, delivery and payments
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => handleSpocManagementDialogBox(true)}
                  className="h-8 px-3.5 rounded-[7px] border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] text-xs font-medium inline-flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm self-start sm:self-auto"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add POC</span>
                </button>
              </div>

              {/* Table Structure */}
              <div className="border border-[#dde3e4] rounded-[10px] overflow-hidden bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-[#f6f8f8] border-b border-[#dde3e4] text-[11px] font-semibold uppercase tracking-wider text-[#526066]">
                        <th className="py-3 px-4">Name</th>
                        <th className="py-3 px-4">Designation</th>
                        <th className="py-3 px-4">Mobile</th>
                        <th className="py-3 px-4">Email</th>
                        <th className="py-3 px-4">Department</th>
                        <th className="py-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#eaeeef] text-[13px]">
                      {spocs.length > 0 ? (
                        spocs.map((spoc, idx) => {
                          const isPrimary = Boolean(
                            Number(spoc.is_primary) === 1 ||
                            (!spocs.some((s) => Number(s.is_primary) === 1) && idx === 0)
                          );
                          return (
                            <tr
                              key={spoc.id || idx}
                              className="hover:bg-[#fbfcfc] transition-colors"
                            >
                              {/* Name */}
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-2.5">
                                  <div className="w-8 h-8 rounded-full bg-[#d7ecef] text-[#246b76] flex items-center justify-center font-bold text-xs shrink-0">
                                    {getInitials(spoc.name)}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="font-semibold text-[#151b1d] truncate">
                                        {spoc.name}
                                      </span>
                                      {isPrimary && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#246b76] bg-[#d7ecef] px-2 py-0.5 rounded-full shrink-0">
                                          <span className="w-1.5 h-1.5 rounded-full bg-[#246b76]" />
                                          Primary
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* Designation */}
                              <td className="py-3 px-4 text-[#151b1d] font-medium">
                                {spoc.designation || <span className="text-[#9aa6ab]">-</span>}
                              </td>

                              {/* Mobile */}
                              <td className="py-3 px-4 text-[#151b1d]">
                                {spoc.mobile ? (
                                  <span className="inline-flex items-center gap-1.5 text-xs">
                                    <Phone className="w-3.5 h-3.5 text-[#7c8b91] shrink-0" />
                                    <span>{spoc.mobile}</span>
                                  </span>
                                ) : (
                                  <span className="text-[#9aa6ab]">-</span>
                                )}
                              </td>

                              {/* Email */}
                              <td className="py-3 px-4 text-[#526066]">
                                {spoc.email ? (
                                  <span className="inline-flex items-center gap-1.5 text-xs">
                                    <Mail className="w-3.5 h-3.5 text-[#7c8b91] shrink-0" />
                                    <span className="truncate max-w-[200px]">{spoc.email}</span>
                                  </span>
                                ) : (
                                  <span className="text-[#9aa6ab]">-</span>
                                )}
                              </td>

                              {/* Department */}
                              <td className="py-3 px-4 text-[#526066]">
                                {spoc.department ? (
                                  <span className="inline-flex items-center px-2 py-0.5 bg-[#f0f4f5] text-[#415158] rounded text-xs font-medium">
                                    {spoc.department}
                                  </span>
                                ) : (
                                  <span className="text-[#9aa6ab]">-</span>
                                )}
                              </td>

                              {/* Actions */}
                              <td className="py-3 px-4 text-right">
                                <div className="flex items-center justify-end gap-1.5">
                                  {!isPrimary && (
                                    <button
                                      type="button"
                                      onClick={() => handleMakePrimarySpoc(idx)}
                                      className="text-xs font-semibold text-[#246b76] hover:underline cursor-pointer mr-2"
                                    >
                                      Make primary
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSpocPendingEdit(spoc);
                                      setSPOCManagementOpen(true);
                                      setAddSPOCManagementOpen(false);
                                    }}
                                    className="w-7 h-7 rounded flex items-center justify-center text-[#7c8b91] hover:text-[#246b76] hover:bg-[#f6f8f8] cursor-pointer transition-colors"
                                    title="Edit contact"
                                  >
                                    <Edit3 className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setSpocPendingDelete({ id: String(spoc.id), name: spoc.name })}
                                    className="w-7 h-7 rounded flex items-center justify-center text-[#7c8b91] hover:text-[#b0453f] hover:bg-[#fdf2f2] cursor-pointer transition-colors"
                                    title="Delete contact"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={6} className="py-8 text-center text-xs text-[#7c8b91]">
                            No contacts added yet. Click &quot;Add POC&quot; above to add one.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Section: Addresses (2 boxes per row) */}
          {activeSection === "address" && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-[16px] font-semibold text-[#151b1d]">
                      Addresses
                    </h2>
                    <span className="px-2 py-0.5 bg-[#eaeff0] text-[#526066] rounded text-[11px] font-semibold">
                      {addresses.length}
                    </span>
                  </div>
                  <p className="text-xs text-[#7c8b91] mt-0.5">
                    Registered, billing and delivery locations
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setAddressPendingEdit(null);
                    setAddAddressManagementOpen(true);
                    setAddressManagementOpen(true);
                  }}
                  className="h-8 px-3.5 rounded-[7px] border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] text-xs font-medium inline-flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm self-start sm:self-auto"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Add Address</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {addresses.length > 0 ? (
                  addresses.map((addr, idx) => {
                    const isPrimary = Boolean(
                      Number(addr.is_primary) === 1 ||
                      (!addresses.some((a) => Number(a.is_primary) === 1) && idx === 0)
                    );
                    return (
                      <div
                        key={addr.id || idx}
                        className="border border-[#dde3e4] rounded-[10px] p-4 flex flex-col justify-between bg-white hover:border-[#b0c8cc] transition-colors gap-3"
                      >
                        <div>
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-semibold px-2 py-0.5 bg-[#eaeff0] text-[#526066] rounded">
                                {addr.address_type || "Registered Office"}
                              </span>
                              {isPrimary && (
                                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#246b76] bg-[#d7ecef] px-2.5 py-0.5 rounded-full">
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#246b76]" />
                                  Primary
                                </span>
                              )}
                            </div>
                            <div className="w-8 h-8 rounded-full bg-[#d7ecef] text-[#246b76] flex items-center justify-center shrink-0">
                              <MapPin className="w-4 h-4" />
                            </div>
                          </div>

                          {addr.gstin && (
                            <div className="text-[11.5px] text-[#526066] mt-2">
                              GSTIN: <span className="font-semibold text-[#151b1d]">{addr.gstin}</span>
                            </div>
                          )}

                          <p className="text-[13px] text-[#151b1d] font-medium mt-1.5 leading-snug">
                            {addr.address_line1}
                            {addr.address_line2 ? `, ${addr.address_line2}` : ""}
                          </p>
                          <p className="text-xs text-[#7c8b91] mt-1">
                            {addr.city}, {addr.state} - {addr.pincode}
                          </p>
                        </div>

                        <div className="flex items-center justify-between pt-2.5 border-t border-[#eaeeef] mt-auto">
                          <div>
                            {!isPrimary && (
                              <button
                                type="button"
                                onClick={() => handleMakePrimaryAddress(idx)}
                                className="text-xs font-semibold text-[#246b76] hover:underline cursor-pointer"
                              >
                                Make primary
                              </button>
                            )}
                          </div>

                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => {
                                setAddressPendingEdit(addr);
                                setAddAddressManagementOpen(false);
                                setAddressManagementOpen(true);
                              }}
                              className="w-7 h-7 rounded flex items-center justify-center text-[#7c8b91] hover:text-[#246b76] hover:bg-[#f6f8f8] cursor-pointer transition-colors"
                              title="Edit address"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>

                            <button
                              type="button"
                              onClick={() => setAddressPendingDelete(addr)}
                              className="w-7 h-7 rounded flex items-center justify-center text-[#7c8b91] hover:text-[#b0453f] hover:bg-[#fdf2f2] cursor-pointer transition-colors"
                              title="Delete address"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="col-span-1 md:col-span-2 border border-dashed border-[#dde3e4] bg-[#f6f8f8] rounded-[10px] p-6 text-center text-xs text-[#526066]">
                    No addresses registered yet. Click &quot;Add Address&quot; above to add one.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Section: Bank Account */}
          {activeSection === "bank" && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-[16px] font-semibold text-[#151b1d]">
                      Bank accounts
                    </h2>
                    <span className="px-2 py-0.5 bg-[#eaeff0] text-[#526066] rounded text-[11px] font-semibold">
                      {hasBankDetails ? 1 : 0}
                    </span>
                  </div>
                  <p className="text-xs text-[#7c8b91] mt-0.5">
                    Accounts used for supplier payouts
                  </p>
                </div>

                {!hasBankDetails && (
                  <button
                    type="button"
                    onClick={() => setEditBankDetailsOpen(true)}
                    className="h-8 px-3.5 rounded-[7px] border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] text-xs font-medium inline-flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm self-start sm:self-auto"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Bank Account</span>
                  </button>
                )}
              </div>

              {hasBankDetails ? (
                <div className="border border-[#dde3e4] rounded-[10px] bg-white overflow-hidden">
                  <div className="p-4 flex items-start gap-3 border-b border-[#eaeeef]">
                    <div className="w-10 h-10 rounded-[8px] bg-[#e1f0f1] text-[#2e6b76] flex items-center justify-center shrink-0">
                      <Landmark className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[14px] font-semibold text-[#151b1d]">
                          {bankName || "Bank"} · {maskedAccNo || "—"}
                        </p>
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#e2f2e8] px-2 py-0.5 text-[10.5px] font-semibold text-[#227a4c]">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#1e9a52]" />
                          Verified
                        </span>
                      </div>
                      <p className="text-[11px] text-[#526066] mt-0.5 break-words">
                        {[
                          holderName || "—",
                          ifsc ? `IFSC ${ifsc}` : null,
                          bankVerifiedAt ? `Verified on ${bankVerifiedAt}` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                  </div>

                  <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                    <div>
                      <p className="text-[11px] text-[#7c8b91] mb-0.5">Account holder name</p>
                      <p className="text-[13px] font-semibold text-[#151b1d] break-words">
                        {holderName || "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-[#7c8b91] mb-0.5">Account number</p>
                      <p className="text-[13px] font-semibold text-[#151b1d] inline-flex items-center gap-1.5">
                        <span className="tabular-nums">{displayAccNo || "—"}</span>
                        {rawAccNo && (
                          <button
                            type="button"
                            onClick={() => setShowBankAccountNo((v) => !v)}
                            className="text-[#7c8b91] hover:text-[#3f8b98] cursor-pointer"
                            title={showBankAccountNo ? "Hide account number" : "Show account number"}
                          >
                            {showBankAccountNo ? (
                              <EyeOff className="w-3.5 h-3.5" />
                            ) : (
                              <Eye className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-[#7c8b91] mb-0.5">IFSC code</p>
                      <p className="text-[13px] font-semibold text-[#151b1d] inline-flex items-center gap-1.5">
                        {ifsc || "—"}
                        {ifsc && (
                          <button
                            type="button"
                            onClick={() => copyToClipboard(ifsc, "IFSC")}
                            className="text-[#7c8b91] hover:text-[#3f8b98] cursor-pointer"
                            title="Copy IFSC"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-[#7c8b91] mb-0.5">Account validation status</p>
                      <p className="text-[13px] font-semibold text-[#227a4c]">{accountStatus}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-[#7c8b91] mb-0.5">Name at the bank</p>
                      <p className="text-[13px] font-semibold text-[#151b1d] inline-flex items-center gap-1.5 break-words">
                        {nameAtBank || "—"}
                        {nameAtBank && (
                          <span className="w-4 h-4 rounded-full bg-[#227a4c] text-white inline-flex items-center justify-center shrink-0">
                            <Check className="w-2.5 h-2.5 stroke-[2.5]" />
                          </span>
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-[#7c8b91] mb-0.5">Bank name</p>
                      <p className="text-[13px] font-semibold text-[#151b1d]">{bankName || "—"}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-[#7c8b91] mb-0.5">Branch name</p>
                      <p className="text-[13px] font-semibold text-[#151b1d] break-words">
                        {branchName || "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-[#7c8b91] mb-0.5">UTR</p>
                      <p className="text-[13px] font-semibold text-[#151b1d] inline-flex items-center gap-1.5 break-all">
                        {bankUtr || "—"}
                        {bankUtr && (
                          <button
                            type="button"
                            onClick={() => copyToClipboard(bankUtr, "UTR")}
                            className="text-[#7c8b91] hover:text-[#3f8b98] cursor-pointer shrink-0"
                            title="Copy UTR"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-[#dde3e4] bg-[#f6f8f8] rounded-[10px] p-8 flex flex-col items-center justify-center text-center">
                  <div className="w-12 h-12 rounded-full bg-white border border-[#dde3e4] text-[#7c8b91] flex items-center justify-center mb-3">
                    <Landmark className="w-6 h-6" />
                  </div>
                  <h3 className="text-sm font-semibold text-[#151b1d]">
                    No bank account added yet
                  </h3>
                  <p className="text-xs text-[#7c8b91] mt-1 max-w-sm">
                    Add the account holder name, account number, and IFSC code so settlements and payouts can be processed.
                  </p>
                  <button
                    type="button"
                    onClick={() => setEditBankDetailsOpen(true)}
                    className="mt-4 h-8 px-4 rounded-[7px] bg-[#347b81] hover:bg-[#286065] text-white text-xs font-medium inline-flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>Add Bank Account</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Section: Statutory Documents */}
          {activeSection === "documents" && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-[16px] font-semibold text-[#151b1d]">
                      Statutory documents
                    </h2>
                    <span className="px-2 py-0.5 bg-[#eaeff0] text-[#526066] rounded text-[11px] font-semibold">
                      {uploadedDocCount} of {totalBasicDocs}
                    </span>
                  </div>
                  <p className="text-xs text-[#7c8b91] mt-0.5">
                    PAN, GST and Udyam proofs required before payouts
                  </p>
                </div>

                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full self-start sm:self-auto ${uploadedDocCount === totalBasicDocs
                      ? "bg-[#e3f6ea] text-[#1c8a50]"
                      : "bg-[#fef3d6] text-[#b97a1a]"
                    }`}
                >
                  {uploadedDocCount} of {totalBasicDocs} uploaded
                </span>
              </div>

              {/* 3 boxes in one row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(
                  [
                    {
                      type: "pan" as const,
                      title: "PAN Card",
                      defaultFileLabel: "PAN_card.pdf",
                      url: panUrl,
                    },
                    {
                      type: "gst" as const,
                      title: "GST Certificate",
                      defaultFileLabel: "GST_certificate.pdf",
                      url: gstUrl,
                    },
                    {
                      type: "udyam" as const,
                      title: "Udyam Certificate",
                      defaultFileLabel: "Udyam_certificate.pdf",
                      url: udyamUrl,
                    },
                  ] as const
                ).map((doc) => {
                  const localThumb = uploadedThumbnails[doc.type];
                  const hasDoc = Boolean(localThumb?.url || doc.url);
                  const previewUrl = localThumb?.url || doc.url || "";
                  const fileName = localThumb?.name || (doc.url ? doc.defaultFileLabel : "");

                  const ext = (previewUrl.split("?")[0].split(".").pop() || "").toLowerCase();
                  const isImage = localThumb
                    ? localThumb.isImage
                    : ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);
                  const isPdf = localThumb ? localThumb.isPdf : ext === "pdf";

                  return (
                    <div
                      key={doc.type}
                      className="border border-[#dde3e4] rounded-[12px] p-4 bg-white hover:border-[#b0c8cc] transition-all flex flex-col justify-between shadow-sm"
                    >
                      <div>
                        {/* Top: Title & Status Badge */}
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <div
                              className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${hasDoc
                                  ? "bg-[#e3f6ea] text-[#1c8a50]"
                                  : "bg-[#f0f4f5] text-[#526066]"
                                }`}
                            >
                              {hasDoc ? (
                                <Check className="w-4 h-4 stroke-[2.5]" />
                              ) : (
                                <FileText className="w-4 h-4" />
                              )}
                            </div>
                            <span className="text-[13.5px] font-semibold text-[#151b1d] truncate">
                              {doc.title}
                            </span>
                          </div>
                          <span
                            className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${hasDoc
                                ? "bg-[#e3f6ea] text-[#1c8a50]"
                                : "bg-[#fef3d6] text-[#b97a1a]"
                              }`}
                          >
                            {hasDoc ? "Uploaded" : "Pending"}
                          </span>
                        </div>

                        {/* Center: Thumbnail Image Preview OR Empty Upload Dropzone */}
                        {hasDoc ? (
                          <div className="relative group w-full h-36 rounded-[8px] border border-[#dde3e4] bg-[#f8fafb] overflow-hidden flex items-center justify-center mb-2.5">
                            {isImage ? (
                              <img
                                src={previewUrl}
                                alt={doc.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                              />
                            ) : isPdf ? (
                              <div className="w-full h-full relative bg-white flex items-center justify-center">
                                <iframe
                                  src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                                  title={doc.title}
                                  className="w-full h-full pointer-events-none border-0 bg-white"
                                />
                                <div className="absolute top-2 left-2 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow">
                                  PDF
                                </div>
                              </div>
                            ) : (
                              <div className="flex flex-col items-center gap-1.5 p-3 text-center">
                                <FileText className="w-9 h-9 text-[#347b81]" />
                                <span className="text-xs font-semibold text-[#151b1d] truncate max-w-[160px]">
                                  {fileName}
                                </span>
                              </div>
                            )}

                            {/* Hover overlay with Quick Preview button */}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                              <button
                                type="button"
                                onClick={() => void handleViewDocument(doc.type)}
                                className="h-7 px-3 rounded-[6px] bg-white text-[#151b1d] hover:bg-[#f6f8f8] text-xs font-semibold inline-flex items-center gap-1.5 shadow cursor-pointer"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                <span>Preview</span>
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => handleFileUpload(doc.type)}
                            className="w-full h-36 rounded-[8px] border-2 border-dashed border-[#dde3e4] hover:border-[#3f8b98] bg-[#fbfcfc] hover:bg-[#f2f8f8] transition-all flex flex-col items-center justify-center gap-1.5 cursor-pointer p-4 mb-2.5 group"
                          >
                            <div className="w-9 h-9 rounded-full bg-[#eef5f6] text-[#347b81] group-hover:bg-[#d7ecef] group-hover:scale-110 transition-all flex items-center justify-center">
                              <Upload className="w-4 h-4" />
                            </div>
                            <p className="text-xs font-semibold text-[#151b1d]">
                              Click to upload proof
                            </p>
                            <p className="text-[10.5px] text-[#7c8b91] text-center">
                              PDF, PNG, JPG up to 10MB
                            </p>
                          </div>
                        )}

                        {/* File name details */}
                        <div className="text-xs text-[#526066] mb-3 truncate">
                          {hasDoc ? (
                            <span
                              className="font-medium text-[#151b1d] truncate block"
                              title={fileName}
                            >
                              {fileName}
                            </span>
                          ) : (
                            <span className="text-[#9aa6ab]">Proof not uploaded yet</span>
                          )}
                        </div>
                      </div>

                      {/* Bottom Action buttons */}
                      <div className="pt-2.5 border-t border-[#eaeeef] flex items-center justify-between gap-2 mt-auto">
                        {hasDoc ? (
                          <>
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => void handleViewDocument(doc.type)}
                                className="h-7 px-2.5 border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] rounded-[6px] text-xs font-medium inline-flex items-center gap-1 cursor-pointer shadow-sm"
                                title={`View ${doc.title}`}
                              >
                                <Eye className="w-3.5 h-3.5 text-[#526066]" />
                                <span>View</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => void handleDownloadDocument(doc.type)}
                                className="h-7 px-2.5 border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] rounded-[6px] text-xs font-medium inline-flex items-center gap-1 cursor-pointer shadow-sm"
                                title={`Download ${doc.title}`}
                              >
                                <Download className="w-3.5 h-3.5 text-[#526066]" />
                                <span>Download</span>
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={() => setDocPendingDelete(doc.type)}
                              className="w-7 h-7 border border-[#dde3e4] bg-white text-[#b0453f] hover:border-[#b0453f] hover:bg-[#fdf2f2] rounded-[6px] flex items-center justify-center cursor-pointer shadow-sm transition-colors"
                              title={`Delete ${doc.title}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            disabled={uploadingDocType === doc.type}
                            onClick={() => handleFileUpload(doc.type)}
                            className="w-full h-8 border border-[#dde3e4] bg-white hover:border-[#3f8b98] hover:text-[#246b76] text-[#151b1d] rounded-[7px] text-xs font-medium inline-flex items-center justify-center gap-1.5 cursor-pointer shadow-sm transition-colors"
                          >
                            <Upload className="w-3.5 h-3.5 text-[#347b81]" />
                            <span>
                              {uploadingDocType === doc.type ? "Uploading…" : "Upload"}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section: GST Filing Compliance */}
          {activeSection === "gst" && (
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4">
                <div>
                  <h2 className="text-[16px] font-semibold text-[#151b1d]">
                    GST filing compliance
                  </h2>
                  <p className="text-xs text-[#7c8b91] mt-0.5">
                    How punctually this supplier files its monthly returns. Each number is days filed after the due date. Fetched{" "}
                    <span className="font-semibold text-[#151b1d]">{displayGstFetchDate}</span>.
                  </p>
                </div>

                <div className="flex items-center gap-2 self-start sm:self-auto shrink-0">
                  <span className="text-xs font-medium text-[#526066] shrink-0">GSTIN</span>
                  <select
                    value={selectedGstin}
                    onChange={(e) => setSelectedGstin(e.target.value)}
                    className="h-8 min-w-[140px] max-w-[200px] px-2.5 rounded-[7px] border border-[#dde3e4] bg-white text-xs text-[#151b1d] outline-none focus:border-[#3f8b98]"
                  >
                    <option value="all">
                      All GSTINs ({supplierGstins.length})
                    </option>
                    {supplierGstins.map((gstin) => (
                      <option key={gstin} value={gstin}>
                        {gstin}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => void handleRefreshGst()}
                    disabled={gstRefreshing || supplierGstins.length === 0}
                    className="h-8 px-3 rounded-[7px] border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] text-xs font-medium inline-flex items-center gap-1.5 transition-colors cursor-pointer shadow-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 text-[#526066] ${gstRefreshing ? "animate-spin text-[#3f8b98]" : ""}`} />
                    <span>{gstRefreshing ? "Refreshing…" : "Refresh"}</span>
                  </button>
                </div>
              </div>

              {/* GST Content Body */}
              <div className="space-y-4">
                {gstLoading ? (
                  <div className="flex items-center justify-center py-10 text-[#7c8b91] text-xs">
                    <RefreshCw className="w-4 h-4 animate-spin text-[#3f8b98] mr-2" />
                    Loading GST filing status…
                  </div>
                ) : null}

                {/* GST 2 Cards Grid: GSTR-1 and GSTR-3B */}
                <div className={`grid grid-cols-1 xl:grid-cols-2 gap-6 ${gstLoading ? "opacity-50 pointer-events-none" : ""}`}>
                  {gstDisplayData.map((gst) => {
                    const stats = calcGstStats(gst.years);
                    return (
                      <div key={gst.title} className="flex flex-col justify-between w-full min-w-0">
                        <h3 className="text-[13px] font-semibold text-[#151b1d] mb-2.5">
                          {gst.title}
                        </h3>

                        {/* 3 Metric Mini Cards */}
                        <div className="grid grid-cols-3 gap-2.5 mb-3">
                          <div className="border border-[#dde3e4] rounded-[8px] p-2.5 bg-white">
                            <small className="block text-[10.5px] font-medium text-[#526066] truncate">
                              On time · {stats.ok} of {stats.filed}
                            </small>
                            <b className="block text-[18px] font-semibold text-[#151b1d] leading-tight mt-0.5">
                              {stats.onTimePercent}%
                            </b>
                            <div className="h-[3px] bg-[#dde3e4] rounded-full overflow-hidden mt-1.5">
                              <div
                                className="h-full bg-[#347b81] rounded-full"
                                style={{ width: `${stats.onTimePercent}%` }}
                              />
                            </div>
                          </div>

                          <div className="border border-[#dde3e4] rounded-[8px] p-2.5 bg-white">
                            <small className="block text-[10.5px] font-medium text-[#526066] truncate">
                              Days late in total
                            </small>
                            <b
                              className={`block text-[18px] font-semibold leading-tight mt-0.5 ${stats.days > 0 ? "text-[#b0453f]" : "text-[#151b1d]"
                                }`}
                            >
                              {stats.days}
                            </b>
                          </div>

                          <div className="border border-[#dde3e4] rounded-[8px] p-2.5 bg-white">
                            <small className="block text-[10.5px] font-medium text-[#526066] truncate">
                              Periods with no record
                            </small>
                            <b
                              className={`block text-[18px] font-semibold leading-tight mt-0.5 ${stats.nr > 0 ? "text-[#b0453f]" : "text-[#151b1d]"
                                }`}
                            >
                              {stats.nr}
                            </b>
                          </div>
                        </div>

                        {/* Calendar Matrix Table */}
                        <div className="border border-[#dde3e4] rounded-[8px] overflow-x-auto w-full">
                          <div className="min-w-[460px]">
                            {/* Header Month Row */}
                            <div className="grid grid-cols-[48px_repeat(12,1fr)] items-center text-center bg-[#e1f0f1] text-[#526066] text-[10.5px] font-medium h-8 border-b border-[#dde3e4]">
                              <div className="text-left pl-3 font-semibold text-[#2e6b76]">Year</div>
                              {MON.map((m) => (
                                <div key={m} className="truncate">{m}</div>
                              ))}
                            </div>

                            {/* Year Rows */}
                            {gst.years.map(([year, months], idx) => (
                              <div
                                key={year}
                                className={`grid grid-cols-[48px_repeat(12,1fr)] items-center text-center h-9 ${idx > 0 ? "border-t border-[#eaeeef]" : ""
                                  } ${idx === 1 ? "bg-[#fafcfc]" : "bg-white"}`}
                              >
                                <div className="text-left pl-3 font-semibold text-[11.5px] text-[#151b1d]">
                                  {year}
                                </div>
                                {months.map((val, mIdx) => {
                                  const monthName = `${MON[mIdx]} ${year}`;
                                  let tip = `${monthName} · Filed on time`;
                                  let dotContent = <Check className="w-3 h-3 stroke-[2.5]" />;
                                  let dotClass = "bg-[#e2f2e8] text-[#227a4c]";

                                  if (val === "?") {
                                    tip = `${monthName} · No filing record found`;
                                    dotContent = <span>?</span>;
                                    dotClass = "border border-dashed border-[#b0453f] text-[#b0453f] bg-white";
                                  } else if (val === "-") {
                                    tip = `${monthName} · Not applicable`;
                                    return (
                                      <div
                                        key={mIdx}
                                        className="text-center text-[#7c8b91] text-xs cursor-default"
                                        onMouseEnter={(e) => {
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          setActiveTooltip({ text: tip, x: rect.left + rect.width / 2, y: rect.top - 8 });
                                        }}
                                        onMouseLeave={() => setActiveTooltip(null)}
                                      >
                                        –
                                      </div>
                                    );
                                  } else if (typeof val === "number") {
                                    tip = `${monthName} · Filed ${val} day${val > 1 ? "s" : ""} late`;
                                    dotContent = <span>{val}</span>;
                                    if (val <= 5) {
                                      dotClass = "bg-[#f3e7c9] text-[#8c6500]";
                                    } else if (val <= 15) {
                                      dotClass = "bg-[#8c6500] text-white";
                                    } else {
                                      dotClass = "bg-[#b0453f] text-white";
                                    }
                                  }

                                  return (
                                    <div key={mIdx} className="flex items-center justify-center">
                                      <button
                                        type="button"
                                        className={`w-5.5 h-5.5 rounded-full flex items-center justify-center text-[10.5px] font-semibold transition-transform hover:scale-115 cursor-default ${dotClass}`}
                                        onMouseEnter={(e) => {
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          setActiveTooltip({ text: tip, x: rect.left + rect.width / 2, y: rect.top - 8 });
                                        }}
                                        onMouseLeave={() => setActiveTooltip(null)}
                                      >
                                        {dotContent}
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* GST Legend */}
                <div className="flex items-center gap-4 flex-wrap text-[11px] text-[#526066] pt-1">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full bg-[#e2f2e8] text-[#227a4c] flex items-center justify-center text-[9px] font-semibold">
                      <Check className="w-2.5 h-2.5 stroke-[2.5]" />
                    </span>
                    On time
                  </span>

                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full bg-[#f3e7c9] text-[#8c6500] flex items-center justify-center text-[9px] font-semibold">
                      n
                    </span>
                    Low risk · 1 to 5 days late
                  </span>

                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full bg-[#8c6500] text-white flex items-center justify-center text-[9px] font-semibold">
                      n
                    </span>
                    Medium risk · 6 to 15 days late
                  </span>

                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full bg-[#b0453f] text-white flex items-center justify-center text-[9px] font-semibold">
                      n
                    </span>
                    High risk · over 15 days late
                  </span>

                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full border border-dashed border-[#b0453f] text-[#b0453f] flex items-center justify-center text-[9px] font-semibold">
                      ?
                    </span>
                    No filing record
                  </span>

                  <span className="inline-flex items-center gap-1.5">
                    <span className="text-[#7c8b91] font-semibold">–</span>
                    Not applicable
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
